import React, { useState, useMemo, useEffect } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Typography,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  LinearProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AssessmentIcon from "@mui/icons-material/Assessment";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FilterListIcon from "@mui/icons-material/FilterList";
import EventIcon from "@mui/icons-material/Event";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useDispatch, useSelector } from "react-redux";
import { fetchJobHours, fetchIwoGroupByAdditional } from "../../actions/ProductionAction";
import { fetchPmIwoFrames } from "../../actions/ProductionMonitorAction";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import homeImage from "../../assets/DTS2_Landscape.png";

// ─── Shared Styled Components ──────────────────────────────────────────────────

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: "12px",
  boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
  border: "1px solid #e2e8f0",
  backgroundColor: "#ffffff",
  maxHeight: "560px",
  overflowY: "auto",
  overflowX: "auto",
  [theme.breakpoints.down("sm")]: {
    maxHeight: "calc(100vh - 300px)",
    borderRadius: "10px",
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontFamily: "'Poppins', sans-serif",
  fontSize: "13px",
  padding: "12px 14px",
  textAlign: "center",
  "&.MuiTableCell-head": {
    backgroundColor: "#154c79",
    color: "#ffffff",
    fontWeight: 600,
    fontSize: "13px",
    position: "sticky",
    top: 0,
    zIndex: 10,
    letterSpacing: "0.3px",
    whiteSpace: "nowrap",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  transition: "background 0.15s ease",
  "&:nth-of-type(odd)": {
    backgroundColor: "#f8fafc",
  },
  "&:hover": {
    backgroundColor: "rgba(21, 76, 121, 0.07)",
    cursor: "pointer",
  },
}));

// ─── Results View Styled Components ───────────────────────────────────────────

const ResultsTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: "12px",
  border: "1px solid #e2e8f0",
  backgroundColor: "#ffffff",
  maxHeight: "320px",
  overflow: "auto",
  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  [theme.breakpoints.down("sm")]: {
    borderRadius: "10px",
    maxHeight: "280px",
  },
}));

const ResultsTableCell = styled(TableCell)(({ theme }) => ({
  fontFamily: "'Poppins', sans-serif",
  fontSize: "13px",
  padding: "11px 12px",
  textAlign: "center",
  borderBottom: "1px solid #f1f5f9",
  color: "#334155",
  "&.MuiTableCell-head": {
    backgroundColor: "#154c79",
    color: "#ffffff",
    fontWeight: 600,
    fontSize: "12px",
    position: "sticky",
    top: 0,
    zIndex: 10,
    borderBottom: "none",
    letterSpacing: "0.4px",
    whiteSpace: "nowrap",
    padding: "13px 12px",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "11px",
    padding: "9px 8px",
    "&.MuiTableCell-head": {
      fontSize: "11px",
      padding: "11px 8px",
    },
  },
}));

const ResultsTableRow = styled(TableRow)(() => ({
  transition: "background 0.15s ease",
  "&:nth-of-type(odd)": {
    backgroundColor: "#f8fafc",
  },
  "&:hover": {
    backgroundColor: "rgba(21, 76, 121, 0.06)",
  },
  "&:last-child td": {
    borderBottom: "none",
  },
}));

//Summary Cards

const SummaryCard = styled(Box)(({ theme }) => ({
  borderRadius: "12px",
  padding: "10px 14px",
  color: "#ffffff",
  boxShadow: "0px 3px 10px rgba(0,0,0,0.12)",
  flexShrink: 0,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  minWidth: 0,
  [theme.breakpoints.down("sm")]: {
    padding: "8px 10px",
    borderRadius: "10px",
  },
}));

const SummaryLabel = styled(Typography)(({ theme }) => ({
  fontFamily: "'Poppins', sans-serif",
  fontSize: "11px",
  fontWeight: 500,
  marginBottom: "6px",
  display: "flex",
  alignItems: "center",
  gap: "5px",
  opacity: 0.88,
  letterSpacing: "0.3px",
  textTransform: "uppercase",
  [theme.breakpoints.down("sm")]: {
    fontSize: "10px",
    marginBottom: "4px",
  },
}));

const SummaryValue = styled(Typography)(({ theme }) => ({
  fontFamily: "'Poppins', sans-serif",
  fontSize: "22px",
  fontWeight: 700,
  lineHeight: 1.15,
  [theme.breakpoints.down("sm")]: {
    fontSize: "18px",
  },
}));

// Main Component
export default function AccumulatedTable({ rows = [] }) {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery("(max-width:768px)");

  const { JobHoursData, jobHoursLoading, IwoGroupData, iwoGroupLoading } = useSelector(
    (state) => state.productionAccumulate
  );

  const [search, setSearch] = useState("");
  const [selectedIwo, setSelectedIwo] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [selectedJCat, setSelectedJCat] = useState("");
  const [selectedJMain, setSelectedJMain] = useState("");
  const [selectedAdditional, setSelectedAdditional] = useState("");

  // Frames of the selected IWO(s) (GetFrameDetails?iwoNo=xx)
  const { iwoFrames, iwoFramesLoading, iwoFramesError } = useSelector((state) => state.productionMonitor);

  const frameSummary = useMemo(() => {
    const byId = new Map();
    (iwoFrames || []).forEach((f) => byId.set(String(f.FrameId), f)); // count by FrameId
    const frames = Array.from(byId.values());
    const total = frames.length;
    const completed = frames.filter((f) => f.CompletionStatus === true).length;

    // per IWO (shown when the group has more than one IWO)
    const perIwo = {};
    frames.forEach((f) => {
      const k = String(f.IwoNo);
      if (!perIwo[k]) perIwo[k] = { total: 0, completed: 0 };
      perIwo[k].total += 1;
      if (f.CompletionStatus === true) perIwo[k].completed += 1;
    });

    return {
      total,
      completed,
      remaining: total - completed,
      percent: total > 0 ? Math.round((completed / total) * 100) : 0,
      perIwo: Object.entries(perIwo).sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true })),
    };
  }, [iwoFrames]);

  useEffect(() => {
    dispatch(fetchIwoGroupByAdditional());
  }, [dispatch]);

  const formatDate = (date) => new Date(date).toISOString().split("T")[0];
  const formatTime = (date) =>
    new Date(date).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const getTodayDateDisplay = () => new Date().toISOString().split("T")[0];

  const formatDecimal = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDecimalNo = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return num.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const sortedJobHoursData = useMemo(() => {
  if (!JobHoursData || JobHoursData.length === 0) return [];
  return [...JobHoursData].sort((a, b) =>
    String(a.IwoNo).localeCompare(String(b.IwoNo), undefined, { numeric: true })
  );
}, [JobHoursData]);

  const {
    totalEstimatedHours,
    totalActualHours,
    totalDifference,
    actualPercentage,
    balancePercentage,
  } = useMemo(() => {
    if (!JobHoursData || JobHoursData.length === 0)
      return { totalEstimatedHours: 0, totalActualHours: 0, totalDifference: 0, actualPercentage: 0, balancePercentage: 0 };
    const estimated = JobHoursData.reduce((sum, item) => sum + (parseFloat(item.EstimatedHours) || 0), 0);
    const actual = JobHoursData.reduce((sum, item) => sum + (parseFloat(item.ActualHours) || 0), 0);
    const difference = estimated - actual;
    return {
      totalEstimatedHours: estimated.toFixed(2),
      totalActualHours: actual.toFixed(2),
      totalDifference: difference.toFixed(2),
      actualPercentage: (estimated > 0 ? (actual / estimated) * 100 : 0).toFixed(0),
      balancePercentage: (estimated > 0 ? (difference / estimated) * 100 : 0).toFixed(0),
    };
  }, [JobHoursData]);

  // shared UnitTurnTargetDate
  const unitTurnTargetDate = useMemo(() => {
    if (!JobHoursData || JobHoursData.length === 0) return null;
    return JobHoursData[0]?.UnitTurnTargetDate || null;
  }, [JobHoursData]);

  const uniqueRows = useMemo(() => {
    const map = new Map();
    const safeRows = Array.isArray(rows) ? rows : Object.values(rows);
    safeRows.forEach((row) => { if (!map.has(row.iwo_no)) map.set(row.iwo_no, row); });
    return Array.from(map.values());
  }, [rows]);

  const uniqueJCats = useMemo(() => {
    return [...new Set(uniqueRows.map((r) => r.JCat).filter(Boolean))].sort();
  }, [uniqueRows]);

  const uniqueJMains = useMemo(() => {
    if (!selectedJCat) return [];
    return [...new Set(uniqueRows.filter((r) => r.JCat === selectedJCat).map((r) => r.JMain).filter(Boolean))].sort();
  }, [uniqueRows, selectedJCat]);

  const filteredRows = useMemo(() => {
    let filtered = uniqueRows;
    if (selectedJCat) filtered = filtered.filter((r) => r.JCat === selectedJCat);
    if (selectedJMain) filtered = filtered.filter((r) => r.JMain === selectedJMain);
    if (search.trim()) {
      const kw = search.trim().toLowerCase();
      filtered = filtered.filter((r) =>
        String(r.iwo_no ?? "").toLowerCase().includes(kw) ||
        String(r.spec ?? "").toLowerCase().includes(kw) ||
        String(r.Supervisor ?? "").toLowerCase().includes(kw)
      );
    }
    return filtered;
  }, [uniqueRows, search, selectedJCat, selectedJMain]);

  const handleCheckboxChange = (iwoNo) => {
    setSelectedIwo((prev) =>
      prev.includes(iwoNo) ? prev.filter((i) => i !== iwoNo) : [...prev, iwoNo]
    );
  };

  const handleCalculate = async () => {
    if (selectedIwo.length === 0) { alert("Please select at least one IWO"); return; }
    setShowResults(true); // show loading spinner straight away
    await dispatch(fetchJobHours(selectedIwo));
  };

const handleAdditionalChange = async (event) => {
  const additionalName = event.target.value;
  setSelectedAdditional(additionalName);
  if (additionalName) {
    const selectedGroup = IwoGroupData.find((g) => g.Additional === additionalName);
    if (selectedGroup?.IwoList?.length > 0) {
      // Extract just the IwoNo strings from the new object structure
      const iwoNos = selectedGroup.IwoList.map((item) => item.IwoNo);
      setSelectedIwo(iwoNos);
      setShowResults(true); // show results page now -> loading spinner shows until data comes
      dispatch(fetchPmIwoFrames(iwoNos)); // frame count + completed for these IWO(s)
      await dispatch(fetchJobHours(iwoNos));
    }
  } else {
    setSelectedIwo([]);
    setShowResults(false);
  }
};

  const handleBackToSelection = () => { setShowResults(false); setSelectedAdditional(""); setSelectedIwo([]); };
  const handleJCatChange = (e) => { setSelectedJCat(e.target.value); setSelectedJMain(""); };
  const handleJMainChange = (e) => setSelectedJMain(e.target.value);
  const handleClearFilters = () => { setSelectedJCat(""); setSelectedJMain(""); setSearch(""); setSelectedAdditional(""); };

  const handleExportPDF = () => {
    if (!JobHoursData || JobHoursData.length === 0) { alert("No data to export"); return; }
    const doc = new jsPDF("l", "mm", "a4");
    const generatedAt = new Date();
    const pageWidth  = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const additionalName = JobHoursData?.[0]?.Additional || selectedAdditional || "";

    // ── Header image ──
    doc.addImage(homeImage, "PNG", 0, 0, 297, 44);

    // ── Title ──
    doc.setFontSize(20);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(21, 76, 121);
    doc.text("Man Hours Calculation Report", pageWidth / 2, 53, { align: "center" });

    if (additionalName) {
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(21, 76, 121);
      doc.text(additionalName, pageWidth / 2, 61, { align: "center" });
    }

    // ── Divider ──
    const dividerY = additionalName ? 65 : 59;
    doc.setDrawColor(21, 76, 121);
    doc.setLineWidth(0.5);
    doc.line(15, dividerY, pageWidth - 15, dividerY);

    // ── Summary row ──
    const startX = 15;
    const colW   = (pageWidth - 30) / 4;
    const labelY = dividerY + 8;
    const valueY = labelY + 7;

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(120, 120, 120);
    doc.text("Date",               startX,            labelY);
    doc.text("Total Est. Hours",   startX + colW,     labelY);
    doc.text("Total Actual Hours", startX + colW * 2, labelY);
    doc.text("Total Balance",      startX + colW * 3, labelY);

    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(21, 76, 121);
    doc.text(formatDate(generatedAt),                       startX,            valueY);
    doc.text(`${formatDecimalNo(totalEstimatedHours)} hrs`, startX + colW,     valueY);

    doc.setTextColor(22, 163, 74);
    doc.text(`${formatDecimalNo(totalActualHours)} hrs (${actualPercentage}%)`, startX + colW * 2, valueY);

    const balanceNum = parseFloat(totalDifference);
    doc.setTextColor(balanceNum >= 0 ? 202 : 220, balanceNum >= 0 ? 138 : 38, balanceNum >= 0 ? 4 : 38);
    doc.text(`${formatDecimalNo(totalDifference)} hrs (${balancePercentage}%)`, startX + colW * 3, valueY);

    doc.setDrawColor(210, 215, 220);
    doc.setLineWidth(0.3);
    doc.line(15, valueY + 5, pageWidth - 15, valueY + 5);

    // ── Table 1: IWO Details ──
    const detailColumns = [
      "IWO No", "Description", "Total\nDays", "Completed\nDays",
      "Remaining\nDays", "Phy.\nCompletion", "Updated\nDate",
      "Supervisor", "Start\nDate", "End\nDate",
    ];

    const detailRows = JobHoursData.map((item) => {
      const totalDays     = parseFloat(item.TotalDays) || 0;
      const completedDays = parseFloat(item.CompletedDays) || 0;
      const remainingDays = parseFloat(item.RemainingDays) || 0;
      const completedPct  = totalDays > 0 ? ((completedDays / totalDays) * 100).toFixed(1) : "0.0";
      const remainingPct  = totalDays > 0 ? ((remainingDays / totalDays) * 100).toFixed(1) : "0.0";
      const updatedDate   = item.UpdatedDate ? new Date(item.UpdatedDate).toISOString().split("T")[0] : "-";
      const phyCompletion = formatDecimalNo(item.PhysicalCompletion) !== "-" ? `${formatDecimalNo(item.PhysicalCompletion)}%` : "-";
      return [
        item.IwoNo || "",
        item.Description || "",
        formatDecimalNo(item.TotalDays),
        `${formatDecimalNo(item.CompletedDays)}\n(${completedPct}%)`,
        `${formatDecimalNo(item.RemainingDays)}\n(${remainingPct}%)`,
        phyCompletion,
        updatedDate,
        item.Supervisor || "",
        item.JobStartDate || "",
        item.JobEndDate || "",
      ];
    });

    autoTable(doc, {
      head: [detailColumns],
      body: detailRows,
      startY: valueY + 9,
      margin: { left: 15, right: 15 },
      tableWidth: "auto",
      styles: {
        fontSize: 8,
        cellPadding: { top: 4, bottom: 4, left: 3, right: 3 },
        valign: "middle",
        overflow: "linebreak",
        lineColor: [226, 232, 240],
        lineWidth: 0.2,
      },
      headStyles: {
        fillColor: [21, 76, 121],
        textColor: 255,
        fontStyle: "bold",
        halign: "center",
        valign: "middle",
        fontSize: 8,
      },
      columnStyles: {
        0: { cellWidth: 18, halign: "center", fontStyle: "bold" },
        1: { cellWidth: 62, halign: "left" },
        2: { cellWidth: 18, halign: "center" },
        3: { cellWidth: 26, halign: "center" },
        4: { cellWidth: 26, halign: "center" },
        5: { cellWidth: 22, halign: "center" },
        6: { cellWidth: 24, halign: "center" },
        7: { cellWidth: 38, halign: "left" },
        8: { cellWidth: 22, halign: "center" },
        9: { cellWidth: 22, halign: "center" },
      },
      alternateRowStyles: { fillColor: [245, 248, 252] },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 4) {
          const raw = parseFloat(JobHoursData[data.row.index]?.RemainingDays);
          data.cell.styles.textColor = raw >= 0 ? [22, 163, 74] : [220, 38, 38];
          data.cell.styles.fontStyle = "bold";
        }
      },
      didDrawPage: (data) => {
        doc.setFontSize(8);
        doc.setTextColor(160);
        doc.text(
          `Generated on ${formatDate(generatedAt)} ${formatTime(generatedAt)}`,
          pageWidth / 2, pageHeight - 8, { align: "center" }
        );
        doc.text(`Page ${data.pageNumber}`, pageWidth - 15, pageHeight - 8, { align: "right" });
        if (additionalName) doc.text(`Group: ${additionalName}`, 15, pageHeight - 8);
      },
    });

    // ── Table 2: Hours Breakdown ──
    let cursorY = doc.lastAutoTable.finalY + 8;

    // ── Hull Turning Date Banner ──
    if (unitTurnTargetDate) {
      const hullDateStr = new Date(unitTurnTargetDate).toISOString().split("T")[0];
      const bannerH = 9;
      const bannerX = 15;
      const bannerW = pageWidth - 30;

      doc.setFillColor(232, 244, 253);
      doc.setDrawColor(21, 76, 121);
      doc.setLineWidth(0.3);
      doc.roundedRect(bannerX, cursorY, bannerW, bannerH, 2, 2, "FD");

      const textY = cursorY + bannerH / 2 + 1.5;

      doc.setFontSize(9);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(80, 80, 80);
      const labelText = "Hull Turning Date:";
      const labelWidth = doc.getTextWidth(labelText);
      doc.text(labelText, bannerX + 5, textY);

      doc.setFont("helvetica", "bold");
      doc.setTextColor(3, 105, 161);
      doc.text(hullDateStr, bannerX + 5 + labelWidth + 3, textY);

      cursorY += bannerH + 7;
    }

    // ── Section title ──
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(21, 76, 121);
    doc.text("Hours Breakdown", 15, cursorY);

    doc.setDrawColor(21, 76, 121);
    doc.setLineWidth(0.4);
    doc.line(15, cursorY + 2, pageWidth - 15, cursorY + 2);

    const hoursStartY = cursorY + 6;

    // Build hours breakdown rows + totals
    const hoursColumns = ["IWO No", "Estimated Hours", "Actual Hours", "Balance"];

    const hoursRows = JobHoursData.map((item) => {
      const estimated = parseFloat(item.EstimatedHours) || 0;
      const actual    = parseFloat(item.ActualHours) || 0;
      const balance   = estimated - actual;
      return [
        item.IwoNo || "",
        formatDecimalNo(estimated),
        formatDecimalNo(actual),
        formatDecimalNo(balance),
      ];
    });



    autoTable(doc, {
      head: [hoursColumns],
      body: hoursRows,
      startY: hoursStartY,
      margin: { left: 15, right: 15 },
      tableWidth: "auto",
      styles: {
        fontSize: 8,
        cellPadding: { top: 4, bottom: 4, left: 4, right: 4 },
        valign: "middle",
        halign: "center",
        lineColor: [226, 232, 240],
        lineWidth: 0.2,
      },
      headStyles: {
        fillColor: [21, 76, 121],
        textColor: 255,
        fontStyle: "bold",
        halign: "center",
        fontSize: 8,
      },
      columnStyles: {
        0: { cellWidth: 30, fontStyle: "bold", textColor: [21, 76, 121] },
        1: { cellWidth: 40 },
        2: { cellWidth: 40 },
        3: { cellWidth: 40 },
      },
      alternateRowStyles: { fillColor: [245, 248, 252] },
      didParseCell: (data) => {
        if (data.section !== "body") return;
        if (data.column.index === 3) {
          const item = JobHoursData[data.row.index];
          if (item) {
            const bal = (parseFloat(item.EstimatedHours) || 0) - (parseFloat(item.ActualHours) || 0);
            data.cell.styles.textColor = bal >= 0 ? [202, 138, 4] : [220, 38, 38];
            data.cell.styles.fontStyle = "bold";
          }
        }
      },
      didDrawPage: (data) => {
        doc.setFontSize(8);
        doc.setTextColor(160);
        doc.text(
          `Generated on ${formatDate(generatedAt)} ${formatTime(generatedAt)}`,
          pageWidth / 2, pageHeight - 8, { align: "center" }
        );
        doc.text(`Page ${data.pageNumber}`, pageWidth - 15, pageHeight - 8, { align: "right" });
        if (additionalName) doc.text(`Group: ${additionalName}`, 15, pageHeight - 8);
      },
    });

    doc.save(`Man_Hours_Report_${formatDate(generatedAt)}.pdf`);
  };

  //Man Hours Calculation

  if (showResults) {
    // while loading, show the name just chosen (not the previous report)
    const additionalName = selectedAdditional || JobHoursData?.[0]?.Additional || "";
    const balanceNum = parseFloat(totalDifference);

    return (
      <Box sx={{ p: { xs: 1, sm: 1.5 }, backgroundColor: "#f1f5f9" }}>

        {/* ── Header Card ── */}
        <Box sx={{
          backgroundColor: "#154c79",
          borderRadius: "14px",
          padding: { xs: "10px 14px", sm: "12px 18px" },
          mb: 1.5,
          boxShadow: "0px 4px 16px rgba(21,76,121,0.25)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.5, sm: 2 }, minWidth: 0 }}>
            <Box sx={{
              width: { xs: 36, sm: 44 },
              height: { xs: 36, sm: 44 },
              borderRadius: "10px",
              backgroundColor: "rgba(255,255,255,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}>
              <AssessmentIcon sx={{ fontSize: { xs: 20, sm: 26 }, color: "#ffffff" }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{
                color: "rgba(255,255,255,0.65)",
                fontFamily: "'Poppins', sans-serif",
                fontSize: "11px",
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                mb: 0.25,
              }}>
                Man Hours Report · {getTodayDateDisplay()}
              </Typography>
              <Typography sx={{
                color: "#ffffff",
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 700,
                fontSize: { xs: "15px", sm: "19px" },
                lineHeight: 1.2,
                whiteSpace: "normal",
                wordBreak: "break-word",
              }}>
                {additionalName
                ? additionalName
                : JobHoursData?.length === 1
                ? (JobHoursData[0]?.Description || "1 IWO Selected")
                : `${JobHoursData?.length || 0} IWO(s) Selected`}
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleBackToSelection}
            size="small"
            sx={{
              color: "#ffffff",
              backgroundColor: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: "8px",
              flexShrink: 0,
              "&:hover": { backgroundColor: "rgba(255,255,255,0.22)" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {jobHoursLoading ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "380px" }}>
            <CircularProgress size={44} thickness={4} sx={{ color: "#154c79" }} />
            <Typography sx={{ mt: 2.5, color: "#64748b", fontFamily: "'Poppins', sans-serif", fontSize: "14px" }}>
              Calculating man hours…
            </Typography>
          </Box>
        ) : (
          <>
            {/* ── 3 Summary Cards ── */}
            <Box sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: { xs: 0.75, sm: 1 },
              mb: 1.5,
            }}>
              <SummaryCard sx={{ background: "linear-gradient(135deg, #154c79 0%, #1a6099 100%)" }}>
                <SummaryLabel>
                  <AssessmentIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
                  Est. Hours
                </SummaryLabel>
                <SummaryValue sx={{ fontSize: { xs: "17px", sm: "22px" } }}>
                  {formatDecimalNo(totalEstimatedHours)}
                </SummaryValue>
              </SummaryCard>

              <SummaryCard sx={{ background: "linear-gradient(135deg, #16a34a 0%, #22c55e 100%)" }}>
                <SummaryLabel>
                  <AccessTimeIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
                  Actual Hours
                </SummaryLabel>
                <SummaryValue sx={{ fontSize: { xs: "17px", sm: "22px" } }}>
                  {formatDecimalNo(totalActualHours)}
                  <Typography component="span" sx={{ fontSize: { xs: "11px", sm: "13px" }, fontWeight: 600, ml: 0.5, opacity: 0.85 }}>
                    ({actualPercentage}%)
                  </Typography>
                </SummaryValue>
              </SummaryCard>

              <SummaryCard sx={{ background: balanceNum >= 0 ? "linear-gradient(135deg, #d97706 0%, #f59e0b 100%)" : "linear-gradient(135deg, #b91c1c 0%, #dc2626 100%)" }}>
                <SummaryLabel>
                  <AccessTimeIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
                  Balance
                </SummaryLabel>
                <SummaryValue sx={{ fontSize: { xs: "17px", sm: "22px" } }}>
                  {formatDecimalNo(totalDifference)}
                  <Typography component="span" sx={{ fontSize: { xs: "11px", sm: "13px" }, fontWeight: 600, ml: 0.5, opacity: 0.85 }}>
                    ({balancePercentage}%)
                  </Typography>
                </SummaryValue>
              </SummaryCard>
            </Box>

            {/* ── Frames (only for Quick Select by Additional) ── */}
            {selectedAdditional && (
              <Box sx={{ backgroundColor: "#ffffff", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", mb: 1.5, px: { xs: 2, sm: 3 }, py: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
                  <ViewModuleIcon sx={{ fontSize: 18, color: "#154c79" }} />
                  <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "13px", fontWeight: 600, color: "#1e293b" }}>
                    Pre Fabrication Frames
                  </Typography>
                  {!iwoFramesLoading && frameSummary.total > 0 && (
                    <Typography sx={{ ml: "auto", fontFamily: "'Poppins', sans-serif", fontSize: "12px", color: "#64748b" }}>
                      {frameSummary.percent}% completed
                    </Typography>
                  )}
                </Box>

                {iwoFramesLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                    <CircularProgress size={18} sx={{ color: "#154c79" }} />
                    <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "12px", color: "#64748b" }}>Loading frames…</Typography>
                  </Box>
                ) : iwoFramesError ? (
                  <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "12px", color: "#dc2626" }}>{iwoFramesError}</Typography>
                ) : frameSummary.total === 0 ? (
                  <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "12px", color: "#94a3b8" }}>No frames inserted for this IWO yet</Typography>
                ) : (
                  <>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: { xs: 0.75, sm: 1 } }}>
                      {[
                        { label: "Total Frames", value: frameSummary.total, color: "#154c79", bg: "#e8f0fa" },
                        { label: "Completed", value: frameSummary.completed, color: "#16a34a", bg: "#eaf7ee" },
                        { label: "In Progress", value: frameSummary.remaining, color: "#d97706", bg: "#fff6e5" },
                      ].map((c) => (
                        <Box key={c.label} sx={{ backgroundColor: c.bg, borderRadius: "10px", px: { xs: 1, sm: 1.5 }, py: 1, textAlign: "center" }}>
                          <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: { xs: "10px", sm: "11px" }, color: "#64748b", fontWeight: 500 }}>
                            {c.label}
                          </Typography>
                          <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: { xs: "18px", sm: "22px" }, fontWeight: 700, color: c.color, lineHeight: 1.2 }}>
                            {c.value}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <LinearProgress
                      variant="determinate"
                      value={frameSummary.percent}
                      sx={{ mt: 1.25, height: 8, borderRadius: 4, backgroundColor: "#e2e8f0", "& .MuiLinearProgress-bar": { backgroundColor: "#16a34a", borderRadius: 4 } }}
                    />

                    {/* per IWO when the group has more than one */}
                    {frameSummary.perIwo.length > 1 && (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1.25 }}>
                        {frameSummary.perIwo.map(([iwo, v]) => (
                          <Chip
                            key={iwo}
                            size="small"
                            label={`${iwo}: ${v.completed}/${v.total}`}
                            sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "11px", backgroundColor: v.total > 0 && v.completed === v.total ? "#eaf7ee" : "#f1f5f9" }}
                          />
                        ))}
                      </Box>
                    )}
                  </>
                )}
              </Box>
            )}

            {/* ── IWO Details Table ── */}
            <Box sx={{ backgroundColor: "#ffffff", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflow: "hidden", mb: 1.5 }}>
              <Box sx={{ px: { xs: 2, sm: 3 }, py: 1.5, borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "13px", fontWeight: 600, color: "#1e293b" }}>
                  IWO Details
                </Typography>
                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "12px", color: "#64748b" }}>
                  {JobHoursData?.length || 0} records
                </Typography>
              </Box>
              <ResultsTableContainer component={Paper} elevation={0}>
                <Table size="small" sx={{ minWidth: { xs: 640, sm: 760 } }}>
                  <TableHead>
                    <TableRow>
                      <ResultsTableCell sx={{ textAlign: "left !important" }}>Description</ResultsTableCell>
                      <ResultsTableCell>Target Date</ResultsTableCell>
                      <ResultsTableCell
                        style={{
                          width: "80px",
                          whiteSpace: "nowrap",
                          wordBreak: "break-word",
                          textAlign: "center"
                        }}
                      >Remaining Days</ResultsTableCell>
                      <ResultsTableCell
                        style={{
                          width: "80px",
                          minWidth: "70px",
                          whiteSpace: "nowrap",
                          wordBreak: "break-word",
                          textAlign: "center"
                        }}
                      >Phy. Completion </ResultsTableCell>
                      <ResultsTableCell>Total Days</ResultsTableCell>
                      <ResultsTableCell>Completed Days</ResultsTableCell>
                      <ResultsTableCell sx={{ textAlign: "left !important" }}>Supervisor</ResultsTableCell>
                      <ResultsTableCell>IWO No</ResultsTableCell>
                      <ResultsTableCell>Start Date</ResultsTableCell>
                      <ResultsTableCell>End Date</ResultsTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedJobHoursData.map((item, index) => {
                      const totalDays = parseFloat(item.TotalDays) || 0;
                      const completedDays = parseFloat(item.CompletedDays) || 0;
                      const remainingDays = parseFloat(item.RemainingDays) || 0;
                      const completedPct = totalDays > 0 ? ((completedDays / totalDays) * 100).toFixed(1) : "0.0";
                      const remainingPct = totalDays > 0 ? ((remainingDays / totalDays) * 100).toFixed(1) : "0.0";
                      const isNegativeBalance = remainingDays < 0;
                      const updatedDate = item.UpdatedDate ? new Date(item.UpdatedDate).toISOString().split("T")[0] : "-";

                      return (
                        <ResultsTableRow key={index}>
                            <ResultsTableCell sx={{ textAlign: "left !important", maxWidth: "170px", whiteSpace: "normal", wordWrap: "break-word", fontSize: "12px" }}>
                              {item.Description}
                            </ResultsTableCell>
                            <ResultsTableCell sx={{ whiteSpace: "nowrap", fontSize: "12px" }}>{item.JobEndDate}</ResultsTableCell>
                            <ResultsTableCell>
                              <Box sx={{
                                display: "inline-flex",
                                flexDirection: "column",
                                alignItems: "center",
                                backgroundColor: isNegativeBalance ? "rgba(220,38,38,0.08)" : "rgba(22,163,74,0.08)",
                                borderRadius: "8px",
                                px: 1,
                                py: 0.5,
                                minWidth: "56px",
                              }}>
                                <Typography sx={{ fontSize: "14px", fontWeight: 700, color: isNegativeBalance ? "#dc2626" : "#16a34a", fontFamily: "'Poppins', sans-serif", lineHeight: 1.2 }}>
                                  {formatDecimalNo(item.RemainingDays)}
                                </Typography>
                                <Typography sx={{ fontSize: "10px", fontWeight: 600, color: isNegativeBalance ? "#dc2626" : "#16a34a", fontFamily: "'Poppins', sans-serif", opacity: 0.85 }}>
                                  {remainingPct}%
                                </Typography>
                              </Box>
                            </ResultsTableCell>
                            <ResultsTableCell>
                              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.3, minWidth: "60px", }}>
                                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1e293b", fontFamily: "'Poppins', sans-serif" }}>
                                  {formatDecimalNo(item.PhysicalCompletion) !== "-" ? `${formatDecimalNo(item.PhysicalCompletion)}%` : "-"}
                                </Typography>
                                <Typography sx={{ fontSize: "11px", color: "#64748b", fontFamily: "'Poppins', sans-serif" }}>
                                  {updatedDate}
                                </Typography>
                              </Box>
                            </ResultsTableCell>
                            <ResultsTableCell sx={{ fontWeight: 600, color: "#1e293b" }}>
                              {formatDecimalNo(item.TotalDays)}
                            </ResultsTableCell>
                            <ResultsTableCell>
                              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.3 }}>
                                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1e293b", fontFamily: "'Poppins', sans-serif" }}>
                                  {formatDecimalNo(item.CompletedDays)}
                                </Typography>
                                <Typography sx={{ fontSize: "11px", color: "#64748b", fontFamily: "'Poppins', sans-serif" }}>
                                  {completedPct}%
                                </Typography>
                              </Box>
                            </ResultsTableCell>
                            <ResultsTableCell sx={{ textAlign: "left !important", maxWidth: "110px", whiteSpace: "normal", wordWrap: "break-word", fontSize: "12px" }}>
                              {item.Supervisor}
                            </ResultsTableCell>
                            <ResultsTableCell sx={{ fontWeight: 700, color: "#154c79", whiteSpace: "nowrap" }}>
                              {item.IwoNo}
                            </ResultsTableCell>
                            <ResultsTableCell sx={{ whiteSpace: "nowrap", fontSize: "12px" }}>{item.JobStartDate}</ResultsTableCell>
                            <ResultsTableCell sx={{ whiteSpace: "nowrap", fontSize: "12px" }}>{item.JobEndDate}</ResultsTableCell>
                          </ResultsTableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </ResultsTableContainer>
            </Box>

            {/* ── Unit Turn Target Date Banner ── */}
            {unitTurnTargetDate && (
              <Box sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 0.75, sm: 1 },
                mx: 0,
                mb: 1.5,
                py: { xs: "10px", sm: "12px" },
                px: { xs: 2, sm: 3 },
                background: "linear-gradient(90deg, rgba(2,132,199,0.10) 0%, rgba(21,76,121,0.13) 50%, rgba(2,132,199,0.10) 100%)",
                borderRadius: "12px",
                border: "1px dashed rgba(21,76,121,0.35)",
                flexWrap: "wrap",
              }}>
                <EventIcon sx={{ fontSize: { xs: 16, sm: 18 }, color: "#154c79", flexShrink: 0 }} />
                <Typography sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: { xs: "11px", sm: "12px" },
                  fontWeight: 600,
                  color: "#154c79",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  whiteSpace: "nowrap",
                }}>
                  Hull Turning Date:
                </Typography>
                <Typography sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: { xs: "12px", sm: "14px" },
                  fontWeight: 700,
                  fontStyle: "normal",
                  WebkitTextStroke: { xs: "0.4px #0369a1", sm: "0.5px #0369a1" },
                  color: "#0369a1",
                  whiteSpace: "nowrap",
                }}>
                  <strong>
                    {new Date(unitTurnTargetDate).toISOString().split("T")[0]}
                  </strong>
                </Typography>
              </Box>
            )}

            {/* ── Hours Breakdown Table ── */}
            <Box sx={{ backgroundColor: "#ffffff", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)", overflow: "hidden", mb: 1.5 }}>
              <Box sx={{ px: { xs: 2, sm: 3 }, py: 1.5, borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "13px", fontWeight: 600, color: "#1e293b" }}>
                  Hours Breakdown
                </Typography>
                <Typography sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "12px", color: "#64748b" }}>
                  {JobHoursData?.length || 0} records
                </Typography>
              </Box>
              <ResultsTableContainer component={Paper} elevation={0}>
                <Table size="small" sx={{ minWidth: { xs: 360, sm: 480 } }}>
                  <TableHead>
                    <TableRow>
                      <ResultsTableCell>IWO No</ResultsTableCell>
                      <ResultsTableCell>Estimated Hours</ResultsTableCell>
                      <ResultsTableCell>Actual Hours</ResultsTableCell>
                      <ResultsTableCell>Balance</ResultsTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedJobHoursData.map((item, index) => {
                      const estimated = parseFloat(item.EstimatedHours) || 0;
                      const actual = parseFloat(item.ActualHours) || 0;
                      const balance = estimated - actual;
                      const isNegBalance = balance < 0;
                      return (
                        <ResultsTableRow key={index}>
                          <ResultsTableCell sx={{ fontWeight: 700, color: "#154c79", whiteSpace: "nowrap" }}>
                            {item.IwoNo}
                          </ResultsTableCell>
                          <ResultsTableCell sx={{ fontWeight: 600, color: "#1e293b" }}>
                            {formatDecimalNo(estimated)}
                          </ResultsTableCell>
                          <ResultsTableCell>
                            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#16a34a", fontFamily: "'Poppins', sans-serif" }}>
                              {formatDecimalNo(actual)}
                            </Typography>
                          </ResultsTableCell>
                          <ResultsTableCell>
                            <Box sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: isNegBalance ? "rgba(220,38,38,0.08)" : "rgba(245,158,11,0.1)",
                              borderRadius: "8px",
                              px: 1.5,
                              py: 0.5,
                              minWidth: "64px",
                            }}>
                              <Typography sx={{ fontSize: "13px", fontWeight: 700, color: isNegBalance ? "#dc2626" : "#f59e0b", fontFamily: "'Poppins', sans-serif" }}>
                                {formatDecimalNo(balance)}
                              </Typography>
                            </Box>
                          </ResultsTableCell>
                        </ResultsTableRow>
                      );
                    })}

                  </TableBody>
                </Table>
              </ResultsTableContainer>
            </Box>

            {/* ── Action Buttons ── */}
            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1 }}>
              <Button
                onClick={handleExportPDF}
                variant="contained"
                fullWidth
                sx={{
                  backgroundColor: "#16a34a",
                  color: "#ffffff",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 600,
                  fontSize: "14px",
                  py: 1,
                  boxShadow: "0 3px 10px rgba(22,163,74,0.3)",
                  "&:hover": { backgroundColor: "#15803d", boxShadow: "0 4px 14px rgba(22,163,74,0.4)" },
                }}
              >
                Export PDF
              </Button>
              <Button
                onClick={handleBackToSelection}
                variant="outlined"
                fullWidth
                sx={{
                  borderColor: "#154c79",
                  color: "#154c79",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 600,
                  fontSize: "14px",
                  py: 1,
                  borderWidth: "1.5px",
                  "&:hover": { backgroundColor: "rgba(21,76,121,0.05)", borderColor: "#0e3c66", borderWidth: "1.5px" },
                }}
              >
                Back to Production
              </Button>
            </Box>
          </>
        )}
      </Box>
    );
  }

  //IWO Select Page

  return (
    <Box sx={{ p: { xs: 0.5, sm: 1 } }}>

      {/* ── FILTER SECTION ── */}
      <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>

        {/* 1. Quick Select by Additional */}
        <FormControl fullWidth sx={{ mb: { xs: 1.5, sm: 1.5 } }}>
          <InputLabel
            id="additional-label"
            sx={{ color: "#154c79", fontFamily: "'Poppins', sans-serif", "&.Mui-focused": { color: "#154c79" } }}
          >
            Quick Search
          </InputLabel>
          <Select
            labelId="additional-label"
            value={selectedAdditional}
            onChange={handleAdditionalChange}
            label="Quick Select by Additional"
            disabled={iwoGroupLoading}
            sx={{
              borderRadius: "8px",
              backgroundColor: "#ffffff",
              fontFamily: "'Poppins', sans-serif",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: "#154c79", borderWidth: "2px" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#154c79", borderWidth: "2px" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#154c79", borderWidth: "2.5px" },
            }}
          >
            <MenuItem value=""><em>All (Manual Selection)</em></MenuItem>
            {IwoGroupData && IwoGroupData.length > 0 ? (
              [...IwoGroupData]
                .sort((a, b) => {
                  const aDate = a.IwoList?.[0]?.StartDate ?? "";
                  const bDate = b.IwoList?.[0]?.StartDate ?? "";
                  return aDate.localeCompare(bDate);
                })
                .map((group) => (
                <MenuItem key={group.Additional} value={group.Additional} sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "13px" }}>
                  {group.Additional}
                  <Typography component="span" sx={{ ml: 0.75, color: "#94a3b8", fontSize: "12px" }}>
                    ({group.IwoList?.length || 0} IWOs)
                    {group.IwoList?.every(i => i.PhysicalCompletion === "100") ? (
                      <span style={{ color: "#16a34a", marginLeft: 4 }}>Completed</span>
                    ) : (
                      <span style={{ color: "#f59e0b", marginLeft: 4 }}>In Progress</span>
                    )}
                  </Typography>
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled><em>No groups available</em></MenuItem>
            )}
          </Select>
        </FormControl>

        {/* 2. Secondary Filters */}
        <Box sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr" },
          gap: { xs: 1, sm: 1.5 },
          mb: { xs: 1.5, sm: 1.5 },
        }}>
          <FormControl fullWidth>
            <InputLabel id="jcat-label" sx={{ color: "#154c79", fontFamily: "'Poppins', sans-serif", "&.Mui-focused": { color: "#154c79" } }}>
              Job Category
            </InputLabel>
            <Select
              labelId="jcat-label"
              value={selectedJCat}
              onChange={handleJCatChange}
              label="Job Category"
              sx={{
                borderRadius: "8px",
                backgroundColor: "#ffffff",
                fontFamily: "'Poppins', sans-serif",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#154c79", borderWidth: "1.5px" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#154c79", borderWidth: "1.5px" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#154c79", borderWidth: "2px" },
              }}
            >
              <MenuItem value=""><em>All Categories</em></MenuItem>
              {uniqueJCats.map((jcat) => (
                <MenuItem key={jcat} value={jcat} sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "13px" }}>{jcat}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth disabled={!selectedJCat}>
            <InputLabel id="jmain-label" sx={{ color: "#154c79", fontFamily: "'Poppins', sans-serif", "&.Mui-focused": { color: "#154c79" }, "&.Mui-disabled": { color: "rgba(21,76,121,0.35)" } }}>
              Job Main
            </InputLabel>
            <Select
              labelId="jmain-label"
              value={selectedJMain}
              onChange={handleJMainChange}
              label="Job Main"
              sx={{
                borderRadius: "8px",
                backgroundColor: "#ffffff",
                fontFamily: "'Poppins', sans-serif",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#154c79", borderWidth: "1.5px" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#154c79", borderWidth: "1.5px" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#154c79", borderWidth: "2px" },
                "&.Mui-disabled .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(21,76,121,0.25)", borderWidth: "1.5px" },
              }}
            >
              <MenuItem value=""><em>All Mains</em></MenuItem>
              {uniqueJMains.map((jmain) => (
                <MenuItem key={jmain} value={jmain} sx={{ fontFamily: "'Poppins', sans-serif", fontSize: "13px" }}>{jmain}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            fullWidth
            variant="outlined"
            onClick={handleClearFilters}
            startIcon={<FilterListIcon />}
            sx={{
              gridColumn: { xs: "1 / -1", sm: "auto" },
              borderColor: "#154c79",
              color: "#154c79",
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "8px",
              borderWidth: "1.5px",
              fontSize: "14px",
              "&:hover": { borderColor: "#0e3c66", backgroundColor: "rgba(21,76,121,0.04)", borderWidth: "1.5px" },
            }}
          >
            Clear Filters
          </Button>
        </Box>

        {/* Active Filter Chips */}
        {(selectedJCat || selectedJMain || selectedAdditional) && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5, alignItems: "center" }}>
            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#154c79", fontFamily: "'Poppins', sans-serif" }}>
              Active Filters:
            </Typography>
            {selectedAdditional && (
              <Chip label={`Additional: ${selectedAdditional}`} size="small"
                onDelete={() => { setSelectedAdditional(""); setSelectedIwo([]); }}
                sx={{ backgroundColor: "#154c79", color: "#fff", fontSize: "12px", "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.7)" } }} />
            )}
            {selectedJCat && (
              <Chip label={`JCat: ${selectedJCat}`} size="small" onDelete={() => setSelectedJCat("")}
                sx={{ backgroundColor: "#0369a1", color: "#fff", fontSize: "12px", "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.7)" } }} />
            )}
            {selectedJMain && (
              <Chip label={`JMain: ${selectedJMain}`} size="small" onDelete={() => setSelectedJMain("")}
                sx={{ backgroundColor: "#16a34a", color: "#fff", fontSize: "12px", "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.7)" } }} />
            )}
          </Box>
        )}

        {/* 3. Search Bar */}
        <TextField
          fullWidth
          placeholder="Search IWO No, Description, or Supervisor..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#154c79" }} />
              </InputAdornment>
            ),
            endAdornment: search && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>
                  <CloseIcon sx={{ fontSize: 16, color: "#6b7280" }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              backgroundColor: "#ffffff",
              "& fieldset": { borderColor: "#154c79", borderWidth: "1.5px" },
              "&:hover fieldset": { borderColor: "#154c79", borderWidth: "1.5px" },
              "&.Mui-focused fieldset": { borderColor: "#154c79", borderWidth: "2px" },
            },
            "& .MuiInputLabel-root": { color: "#154c79" },
            "& .MuiInputLabel-root.Mui-focused": { color: "#154c79" },
          }}
        />
      </Box>

      {/* ── Selected IWO Chips ── */}
      {selectedIwo.length > 0 && (
        <Box sx={{ mb: 1.5, display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center" }}>
          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#154c79", fontFamily: "'Poppins', sans-serif" }}>
            Selected ({selectedIwo.length}):
          </Typography>
          {selectedIwo.map((iwo) => (
            <Chip key={iwo} label={iwo} size="small" onDelete={() => handleCheckboxChange(iwo)}
              sx={{ height: "24px", backgroundColor: "#154c79", color: "#fff", fontSize: "11px", fontWeight: 600, "& .MuiChip-label": { px: 1 }, "& .MuiChip-deleteIcon": { color: "rgba(255,255,255,0.7)", fontSize: "16px", "&:hover": { color: "#fff" } } }} />
          ))}
        </Box>
      )}

      {/* ── Record Count ── */}
      <Box sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: "13px", color: "#6b7280", fontFamily: "'Poppins', sans-serif" }}>
          Showing {filteredRows.length} of {uniqueRows.length} records
          {selectedIwo.length > 0 && ` • ${selectedIwo.length} selected`}
        </Typography>
      </Box>

      {/* ── MAIN TABLE ── */}
      <StyledTableContainer component={Paper}>
        <Table size="small" sx={{ minWidth: 700 }}>
          <TableHead>
            <TableRow>
              <StyledTableCell />
              <StyledTableCell>IWO No</StyledTableCell>
              <StyledTableCell>Description</StyledTableCell>
              <StyledTableCell>Supervisor</StyledTableCell>
              <StyledTableCell>Job Start Date</StyledTableCell>
              <StyledTableCell>Job End Date</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography sx={{ color: "#6b7280", fontFamily: "'Poppins', sans-serif", fontSize: "14px" }}>
                    No matching data found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredRows.map((row) => (
                <StyledTableRow key={row.iwo_no}>
                  <StyledTableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIwo.includes(row.iwo_no)}
                      onChange={() => handleCheckboxChange(row.iwo_no)}
                      sx={{ color: "#154c79", "&.Mui-checked": { color: "#154c79" } }}
                    />
                  </StyledTableCell>
                  <StyledTableCell sx={{ fontWeight: 600 }}>{row.iwo_no}</StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "left", maxWidth: "200px", whiteSpace: "normal", wordWrap: "break-word" }}>
                    {row.spec}
                  </StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "left" }}>{row.Supervisor}</StyledTableCell>
                  <StyledTableCell>{row.sdate}</StyledTableCell>
                  <StyledTableCell>{row.edate}</StyledTableCell>
                </StyledTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </StyledTableContainer>

      {/* ── CALCULATE BUTTON ── */}
      <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
        <Button
          fullWidth
          variant="contained"
          color="success"
          size={isMobile ? "medium" : "large"}
          sx={{
            borderRadius: "18px",
            fontFamily: "'Poppins', sans-serif",
            fontWeight: "bold",
            textTransform: "none",
            height: { xs: "44px", sm: "52px" },
            backgroundColor: "#16a34a",
            "&:hover": { backgroundColor: "#15803d" },
          }}
          onClick={handleCalculate}
          disabled={selectedIwo.length === 0}
        >
          Calculate Man Hrs {selectedIwo.length > 0 && `(${selectedIwo.length} selected)`}
        </Button>
      </Box>
    </Box>
  );
}